'use client';

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { usePathname, useRouter } from 'next/navigation';

import { useTranslations } from 'next-intl';
import { useAction } from 'next-safe-action/hooks';

import { useSupabase } from '@ci/supabase/hooks/use-supabase';
import { toast } from '@ci/ui/sonner';

import featuresFlagConfig from '~/config/feature-flags.config';
import {
  createRng,
  planCelebration,
  randomSeed,
  readLastCelebrationStyle,
  rememberCelebrationStyle,
} from '~/lib/release-celebrations/celebration-variety';
import {
  type ReleaseNoteEntry,
  buildReleaseNoteSequence,
  interpretReleaseNoteHistory,
} from '~/lib/release-celebrations/release-note-history';
import {
  type RunningRelease,
  WEB_RELEASE_TRACK,
  readRunningRelease,
} from '~/lib/release-celebrations/running-release';
import { setUpdateNoticePreferenceAction } from '~/lib/release-celebrations/update-notice-preference.actions';

import { UpdateNotice } from './update-notice';
import { usePrefersReducedMotion } from './use-dismiss-timer';
import { useReleaseCelebrationClaim } from './use-release-celebration-claim';

/** The signed-in app (both shells, team and personal); never marketing or auth. */
const APP_PATH = /(^|\/)home(\/|$)/;

/** How many earlier notes the arrows can reach. */
const HISTORY_LIMIT = 20;

type ReleaseNotesContextValue = {
  /** Opens the notice on the newest note, with no celebration. */
  open: () => void;
};

const ReleaseNotesContext = createContext<ReleaseNotesContextValue | null>(
  null,
);

/**
 * PAY-4612: opens the release notes on demand (the profile menu's "What's
 * new"). `null` wherever the notice cannot appear (flag off, or outside the
 * signed-in app), and callers then render no entry point at all.
 */
export function useReleaseNotes(): ReleaseNotesContextValue | null {
  return useContext(ReleaseNotesContext);
}

/**
 * PAY-4392: release celebrations on the web (docs/release-celebrations.md).
 *
 * Mounted ONCE, in `RootProviders`, around both navigation shells, so the
 * sidebar and header shells (and every role shell) get it without either
 * layout knowing, and the profile menu in either shell can reach
 * {@link useReleaseNotes}. It renders nothing until the claim says `show` or
 * someone asks for the notes, then portals the notice to `document.body`.
 *
 * The polite live region is rendered from the start, empty, and filled once
 * when the notice appears: a region inserted already holding its text is not
 * reliably announced, and filling it once means it is announced once.
 */
export function ReleaseCelebration({ children }: { children?: ReactNode }) {
  const pathname = usePathname();
  const eligible =
    featuresFlagConfig.enableUpdateCelebrations && APP_PATH.test(pathname);
  // A count of requests, not an open flag: the session that is mounted when
  // a request arrives opens the notes and owns closing them. A session that
  // mounts later (after sign-out, a new sign-in, or a return to /home) starts
  // closed, so a request never outlives the session that made it.
  const [openRequests, setOpenRequests] = useState(0);

  const value = useMemo<ReleaseNotesContextValue>(
    () => ({ open: () => setOpenRequests((count) => count + 1) }),
    [],
  );

  return (
    <ReleaseNotesContext.Provider value={eligible ? value : null}>
      {children}
      {eligible ? (
        <ReleaseCelebrationController openRequests={openRequests} />
      ) : null}
    </ReleaseNotesContext.Provider>
  );
}

function ReleaseCelebrationController({
  openRequests,
}: {
  openRequests: number;
}) {
  const client = useSupabase();
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let authChanged = false;
    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((_event, session) => {
      authChanged = true;
      if (active) setUserId(session?.user.id ?? null);
    });
    void client.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (active && !authChanged) setUserId(session?.user.id ?? null);
      })
      .catch(() => {});
    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [client]);

  return userId ? (
    <UserReleaseCelebration
      key={userId}
      userId={userId}
      openRequests={openRequests}
    />
  ) : null;
}

/**
 * The published notes, newest first, fetched each time the notice opens.
 * A failure (offline, RPC not deployed) is an empty history: the notice
 * still shows the current release alone.
 */
function useReleaseNoteHistory(enabled: boolean) {
  const client = useSupabase();
  const [state, setState] = useState<{
    history: ReleaseNoteEntry[];
    loading: boolean;
  }>({ history: [], loading: false });
  const [prevEnabled, setPrevEnabled] = useState(false);
  if (enabled !== prevEnabled) {
    setPrevEnabled(enabled);
    if (enabled) setState((current) => ({ ...current, loading: true }));
  }

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    void Promise.resolve(
      client.rpc('list_app_release_notes', {
        p_release_track: WEB_RELEASE_TRACK,
        p_limit: HISTORY_LIMIT,
      }),
    )
      .then(({ data, error }) => {
        if (!active) return;
        setState({
          history: error ? [] : interpretReleaseNoteHistory(data),
          loading: false,
        });
      })
      .catch(() => {
        if (active) setState({ history: [], loading: false });
      });
    return () => {
      active = false;
    };
  }, [client, enabled]);

  return state;
}

function UserReleaseCelebration({
  userId,
  openRequests,
}: {
  userId: string;
  openRequests: number;
}) {
  const t = useTranslations('common.updateNotice');
  const router = useRouter();
  const client = useSupabase();
  const [release] = useState<RunningRelease | null>(readRunningRelease);
  const [dismissed, setDismissed] = useState(false);
  // Requests made before this session mounted are not ours to answer.
  const [seenRequests, setSeenRequests] = useState(openRequests);
  const [historyOpen, setHistoryOpen] = useState(false);
  if (openRequests !== seenRequests) {
    setSeenRequests(openRequests);
    setHistoryOpen(true);
  }
  const reducedMotion = usePrefersReducedMotion();

  const claim = useCallback(
    async (running: RunningRelease) => {
      const { data, error } = await client.rpc(
        'claim_app_release_celebration',
        {
          p_release_track: WEB_RELEASE_TRACK,
          p_release_id: running.releaseId,
          p_release_built_at: running.builtAt,
        },
      );
      if (error) throw error;
      return data;
    },
    [client],
  );

  const decision = useReleaseCelebrationClaim({
    eligible: true,
    userId,
    release,
    claim,
  });

  // One plan per notice, drawn when the notice first appears. A dev
  // `?__cycle=1` replaces it with the next style when the canvas finishes.
  const [plan, setPlan] = useState<ReturnType<typeof planCelebration> | null>(
    null,
  );
  const [planned, setPlanned] = useState(false);
  if (decision && !planned) {
    setPlanned(true);
    const running = decision.release;
    const next = planCelebration(createRng(running.seed ?? randomSeed()), {
      style: running.style,
      avoid: running.style ? undefined : readLastCelebrationStyle(),
    });
    setPlan(next);
  }

  useEffect(() => {
    if (plan) rememberCelebrationStyle(plan.style);
  }, [plan]);

  const playNext = useCallback(() => {
    setPlan((current) => {
      if (!current) return current;
      return planCelebration(createRng(randomSeed()), { avoid: current.style });
    });
  }, []);

  const hide = useAction(setUpdateNoticePreferenceAction, {
    onSuccess: () => {
      setDismissed(true);
      router.refresh();
      toast.success(t('hidden'));
    },
    onError: () => {
      toast.error(t('hideFailed'));
    },
  });

  // Asking for "What's new" takes over from a claimed notice that is still
  // open, and a claim that lands while the notes are open is folded into them
  // (it is already receipted, and the history shows its note): either way,
  // closing the card closes both.
  const releaseVisible = decision !== null && !dismissed;
  const mode = historyOpen ? 'history' : releaseVisible ? 'release' : null;
  const { history, loading } = useReleaseNoteHistory(mode !== null);

  const sequence = useMemo(() => {
    if (mode === 'release' && decision) {
      return buildReleaseNoteSequence(history, {
        releaseId: decision.release.releaseId,
        releaseNumber: decision.releaseNumber,
        poem: decision.lines?.[0] ?? null,
        publishedAt: null,
      });
    }
    return buildReleaseNoteSequence(history);
  }, [mode, decision, history]);

  const close = () => {
    if (decision) setDismissed(true);
    setHistoryOpen(false);
  };

  const announcement =
    mode === 'release' && decision
      ? [t('title'), ...(decision.lines ?? [t('noVerse')])].join(' ')
      : '';

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        data-test="update-notice-announcement"
      >
        {announcement}
      </div>
      {mode && typeof document !== 'undefined'
        ? createPortal(
            <UpdateNotice
              key={mode}
              mode={mode}
              entries={sequence.entries}
              startIndex={sequence.startIndex}
              loading={loading}
              plan={mode === 'release' && !reducedMotion ? plan : null}
              hiding={hide.isPending}
              onDismiss={close}
              onHideFuture={() => hide.execute({ showNotices: false })}
              onCelebrationDone={release?.cycle ? playNext : undefined}
            />,
            document.body,
          )
        : null}
    </>
  );
}
